import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { getParentTagCandidates } from "../../helpers/Helpers";
import { hasValue } from "../../../common/helpers/Utils";

const formatParentTagOptionLabel = (option) => {
  if (typeof option === "string" || typeof option === "number") {
    return String(option);
  }

  return `${option.TagName} (${option.TagIdentifier})`;
};

const getOptionTagIdentifier = (option) => {
  if (option && typeof option === "object") {
    return option.TagIdentifier;
  }

  return option;
};

export const ParentTagEditorCell = ({
  value,
  row,
  allTags,
  onCommit,
  onCancel,
}) => {
  const candidateOptions = useMemo(
    () => getParentTagCandidates(row, allTags),
    [row, allTags]
  );
  const matchingOption = useMemo(() => {
    if (!hasValue(value)) {
      return null;
    }

    return (
      candidateOptions.find(
        (option) => String(option.TagIdentifier) === String(value)
      ) ?? null
    );
  }, [candidateOptions, value]);

  const resolvedValue =
    matchingOption ?? (hasValue(value) ? String(value) : null);
  const [freeSoloText, setFreeSoloText] = useState(null);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    setFreeSoloText(null);
  }, [value]);

  const commitValue = (nextValue) => {
    if (nextValue === "" || nextValue == null) {
      onCommit(null);
      return;
    }

    onCommit(nextValue);
  };

  return (
    <Autocomplete
      freeSolo
      size='small'
      disablePortal
      autoFocus
      options={candidateOptions}
      value={resolvedValue}
      onInputChange={(_, nextInputValue, reason) => {
        if (reason === "input") {
          setFreeSoloText(nextInputValue);
        } else if (reason === "reset" || reason === "clear") {
          setFreeSoloText(null);
        }
      }}
      onChange={(_, nextValue) => {
        justSelectedRef.current = true;
        setFreeSoloText(null);

        if (nextValue && typeof nextValue === "object") {
          commitValue(nextValue.TagIdentifier);
          return;
        }

        commitValue(nextValue);
      }}
      onBlur={() => {
        if (justSelectedRef.current) {
          justSelectedRef.current = false;
          return;
        }

        if (freeSoloText === null) {
          return;
        }

        const resolvedInputText = matchingOption
          ? formatParentTagOptionLabel(matchingOption)
          : hasValue(value)
            ? String(value)
            : "";

        if (freeSoloText === resolvedInputText) {
          return;
        }

        commitValue(freeSoloText);
      }}
      getOptionLabel={formatParentTagOptionLabel}
      isOptionEqualToValue={(option, nextValue) => {
        if (nextValue == null) {
          return false;
        }

        return (
          String(option.TagIdentifier) ===
          String(getOptionTagIdentifier(nextValue))
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant='standard'
          placeholder='Select or type a Tag Identifier'
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onCancel();
            }
          }}
        />
      )}
    />
  );
};
